import React, { useState } from 'react';
import axios from 'axios';
import ContactSelector from '../Contacts/ContactSelector';
import ContactForm from '../Contacts/ContactForm';

/**
 * Create Invoice Form - Enhanced with Contact Selection
 * TODO: Add selected contact to invoice after DB integration
 */
export default function CreateInvoiceForm({ onInvoiceCreated }) {
  const [formData, setFormData] = useState({
    customerEmail: '',
    customerName: '',
    description: '',
    dueDate: '',
    items: [{ description: '', amount: '', quantity: 1 }],
  });

  const [selectedContact, setSelectedContact] = useState(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData((prev) => ({
      ...prev,
      items: newItems,
    }));
  };

  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, { description: '', amount: '', quantity: 1 }],
    }));
  };

  const removeItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const calculateTotal = () => {
    return formData.items.reduce((total, item) => {
      const amount = parseFloat(item.amount) || 0;
      const quantity = parseInt(item.quantity) || 1;
      return total + amount * quantity;
    }, 0);
  };

  // Handle contact selection from ContactSelector
  const handleContactSelect = (contact) => {
    if (contact) {
      setSelectedContact(contact);
      // Pre-fill form with contact data
      setFormData((prev) => ({
        ...prev,
        customerEmail: contact.email || prev.customerEmail,
        customerName: contact.name,
        // Store stripe_customer_id if available to link invoice correctly
        stripeCustomerId: contact.stripe_customer_id,
      }));
    }
  };

  // Handle new contact creation
  const handleCreateNewContact = () => {
    setShowContactForm(true);
  };

  // Handle contact form save
  const handleContactFormSave = (newContact) => {
    handleContactSelect(newContact);
    setShowContactForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    // Validation
    if (!formData.customerEmail || !formData.customerName) {
      setError('Email et nom du client sont obligatoires');
      setLoading(false);
      return;
    }

    if (formData.items.length === 0) {
      setError('Au moins un article est requis');
      setLoading(false);
      return;
    }

    for (const item of formData.items) {
      if (!item.description || !item.amount || parseFloat(item.amount) <= 0) {
        setError('Tous les articles doivent avoir une description et un montant valide');
        setLoading(false);
        return;
      }
    }

    try {
      const payload = {
        customerEmail: formData.customerEmail,
        customerName: formData.customerName,
        description: formData.description || undefined,
        dueDate: formData.dueDate || undefined,
        items: formData.items.map((item) => ({
          description: item.description,
          amount: parseFloat(item.amount),
          quantity: parseInt(item.quantity) || 1,
        })),
        // Pass stripeCustomerId to backend to link to existing customer
        stripeCustomerId: formData.stripeCustomerId,
        shippingCost: parseFloat(formData.shippingCost) || 0,
        // TODO: Add contact_id once DB is ready
        // contactId: selectedContact?.id,
      };

      const response = await axios.post('/admin/invoices', payload);
      const data = response.data;

      setSuccess(
        `Facture créée avec succès! Numéro: ${data.invoice.number}`
      );

      // Reset form
      setFormData({
        customerEmail: '',
        customerName: '',
        description: '',
        dueDate: '',
        items: [{ description: '', amount: '', quantity: 1 }],
      });
      setSelectedContact(null);

      // Notify parent component
      if (onInvoiceCreated) {
        onInvoiceCreated(data.invoice);
      }
    } catch (err) {
      console.error('Erreur lors de la création de la facture:', err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-8 p-6">
      <h2 className="text-2xl font-bold text-gray-900">Créer une Facture Manuelle</h2>

      {/* Fermé depuis le changement de vendeur : le serveur refuse aussi. */}
      <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900">
        <p className="font-semibold">Factures manuelles fermées depuis le 23/09/2026</p>
        <p className="mt-1 text-sm">
          Les factures de Sébastien Schoonjans sont émises par le site (série JR-AAAA-NNNN), une par
          vente payée, au moment de l&apos;encaissement. Une facture manuelle Stripe ouvrirait une
          seconde série que la comptabilité ne pourrait pas rapprocher. Les factures déjà émises
          restent consultables dans « Liste des Factures ».
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-green-700">
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Contact Selection Section */}
        <div className="rounded-xl bg-white p-6 shadow-sm">
          <h3 className="mb-4 text-lg font-medium text-gray-900">Sélectionner ou créer un contact</h3>

          <div className="space-y-4">
            <label htmlFor="contact-selector" className="block text-sm font-medium text-gray-700">Rechercher un contact existant</label>
            <ContactSelector
              onContactSelect={handleContactSelect}
              onCreateNew={handleCreateNewContact}
              selectedContact={selectedContact}
            />
          </div>

          {selectedContact && (
            <div className="mt-4 flex items-center rounded-lg bg-blue-50 p-3 text-blue-700">
              <svg className="mr-2 h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Contact sélectionné: <strong>{selectedContact.name}</strong></span>
            </div>
          )}
        </div>

        {/* Client Information */}
        <div className="rounded-xl bg-white p-6 shadow-sm">
          <h3 className="mb-4 text-lg font-medium text-gray-900">Informations du Client</h3>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <label htmlFor="customerEmail" className="block text-sm font-medium text-gray-700">Email du Client *</label>
              <input
                type="email"
                id="customerEmail"
                name="customerEmail"
                value={formData.customerEmail}
                onChange={handleInputChange}
                placeholder="client@example.com"
                required
                className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="customerName" className="block text-sm font-medium text-gray-700">Nom du Client *</label>
              <input
                type="text"
                id="customerName"
                name="customerName"
                value={formData.customerName}
                onChange={handleInputChange}
                placeholder="Jean Dupont"
                required
                className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>

            <div className="md:col-span-2">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description (optionnel)</label>
              <input
                type="text"
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Ex: Facture pour services rendus"
                className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700">Date d'échéance (optionnel)</label>
              <input
                type="date"
                id="dueDate"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="rounded-xl bg-white p-6 shadow-sm">
          <h3 className="mb-4 text-lg font-medium text-gray-900">Articles</h3>

          <div className="space-y-4">
            {formData.items.map((item, index) => (
              <div key={index} className="flex flex-col gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4 sm:flex-row sm:items-start">
                <div className="flex-1">
                  <label htmlFor={`item-desc-${index}`} className="block text-sm font-medium text-gray-700">Description *</label>
                  <input
                    type="text"
                    id={`item-desc-${index}`}
                    value={item.description}
                    onChange={(e) =>
                      handleItemChange(index, 'description', e.target.value)
                    }
                    placeholder="Ex: Consultation"
                    required
                    className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                </div>

                <div className="w-full sm:w-32">
                  <label htmlFor={`item-amount-${index}`} className="block text-sm font-medium text-gray-700">Montant (€) *</label>
                  <input
                    type="number"
                    id={`item-amount-${index}`}
                    value={item.amount}
                    onChange={(e) =>
                      handleItemChange(index, 'amount', e.target.value)
                    }
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    required
                    className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                </div>

                <div className="w-full sm:w-24">
                  <label htmlFor={`item-qty-${index}`} className="block text-sm font-medium text-gray-700">Quantité</label>
                  <input
                    type="number"
                    id={`item-qty-${index}`}
                    value={item.quantity}
                    onChange={(e) =>
                      handleItemChange(index, 'quantity', e.target.value)
                    }
                    min="1"
                    step="1"
                    className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                </div>

                <div className="flex w-full items-center justify-between pt-6 sm:w-auto sm:justify-end">
                  <div className="mr-4 text-sm font-medium text-gray-900 sm:hidden">
                    Sous-total: {(
                      (parseFloat(item.amount) || 0) *
                      (parseInt(item.quantity) || 1)
                    ).toFixed(2)} €
                  </div>

                  <div className="hidden text-right text-sm font-medium text-gray-900 sm:block sm:w-24">
                    {(
                      (parseFloat(item.amount) || 0) *
                      (parseInt(item.quantity) || 1)
                    ).toFixed(2)} €
                  </div>

                  {formData.items.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="ml-4 text-red-600 hover:text-red-800"
                      title="Supprimer cet article"
                    >
                      <span className="sr-only">Supprimer</span>
                      ✕
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={addItem}
            className="mt-4 inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800"
          >
            + Ajouter un article
          </button>
        </div>

        {/* Shipping Cost */}
        <div className="rounded-xl bg-white p-6 shadow-sm">
          <h3 className="mb-4 text-lg font-medium text-gray-900">Livraison</h3>
          <div className="w-full sm:w-48">
            <label htmlFor="shippingCost" className="block text-sm font-medium text-gray-700">Frais de livraison (€)</label>
            <input
              type="number"
              id="shippingCost"
              name="shippingCost"
              value={formData.shippingCost || ''}
              onChange={handleInputChange}
              placeholder="0.00"
              step="0.01"
              min="0"
              className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
        </div>

        {/* Total */}
        <div className="flex items-center justify-end rounded-xl bg-gray-50 p-6">
          <span className="mr-4 text-lg font-medium text-gray-700">Total:</span>
          <span className="text-3xl font-bold text-gray-900">
            {(calculateTotal() + (parseFloat(formData.shippingCost) || 0)).toFixed(2)} €
          </span>
        </div>

        {/* Submit */}
        <div className="flex justify-end">
          <button
            type="submit"
            className="inline-flex justify-center rounded-lg bg-blue-600 px-6 py-3 text-base font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
            // Fermé depuis le 23/09/2026 (voir admin_controller.createInvoice).
            disabled
          >
            {loading ? 'Création en cours...' : 'Créer la Facture'}
          </button>
        </div>
      </form>

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden bg-black/50 p-4 backdrop-blur-sm" onClick={() => setShowContactForm(false)}>
          <div className="relative w-full max-w-2xl rounded-xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b p-4">
              <h3 className="text-lg font-semibold text-gray-900">Créer un nouveau contact</h3>
              <button
                className="text-gray-400 hover:text-gray-500"
                onClick={() => setShowContactForm(false)}
              >
                <span className="sr-only">Fermer</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <ContactForm
                onClose={() => setShowContactForm(false)}
                onSave={handleContactFormSave}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
